import { useCallback, useEffect, useState, useMemo } from 'react';
import { scaleLinear, scaleOrdinal, extent, timeFormat, line } from 'd3';

import { Marks } from './Marks';
import { AxisLeft } from './AxisLeft';
import { AxisBottom } from './AxisBottom';
import { VoronoiOverlay } from './VoronoiOverlay';

const useIsTouchDevice = () => {
  const [isTouch, setIsTouch] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(hover: none)');
    const handler = (e) => setIsTouch(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);
  return isTouch;
};

const xValue = (d) => d.lapNumber;
const yValue = (d) => d.lapTimeSeconds;
const colorValue = (d) => d.compound;

const yAxisTickFormat = (tickValue) => {
  const formatSeconds = timeFormat('%M:%S.%L');
  const base = +new Date(1969, 1, 1);
  return formatSeconds(new Date(base + tickValue * 1000));
};

const BoxedTooltip = ({ markerX, markerY, innerWidth, innerHeight, lines, compoundColor }) => {
  const tooltipW = 168;
  const lineH = 12;
  const padY = 4;
  const padX = 6;
  const cursorOffset = 12;
  const tooltipH = padY + 12 + lines.length * lineH + padY;

  const preferRight = markerX < innerWidth / 2;
  let x = preferRight ? markerX + cursorOffset : markerX - tooltipW - cursorOffset;
  if (preferRight && x + tooltipW > innerWidth) x = markerX - tooltipW - cursorOffset;
  else if (!preferRight && x < 0) x = markerX + cursorOffset;
  x = Math.max(0, Math.min(innerWidth - tooltipW, x));
  const y = Math.max(0, Math.min(innerHeight - tooltipH, markerY + cursorOffset));

  return (
    <g pointerEvents="none">
      <rect
        x={x}
        y={y}
        width={tooltipW}
        height={tooltipH}
        rx={4}
        fill="#0a0a0a"
        stroke="#262626"
        opacity={0.95}
      />
      {lines.map((ln, i) => {
        const ty = y + padY + 9 + i * lineH;
        if (ln.kind === 'header') {
          return (
            <text key={i} x={x + padX} y={ty} fill="#8a8a8a" fontSize={10}>
              {ln.text}
            </text>
          );
        }
        if (ln.kind === 'subtext') {
          return (
            <text
              key={i}
              x={x + padX}
              y={ty}
              fill="#8a8a8a"
              fontSize={10}
              fontStyle="italic"
            >
              {ln.text}
            </text>
          );
        }
        return (
          <text key={i} x={x + padX} y={ty} fontSize={10}>
            {ln.bullet && <tspan fill={compoundColor || '#9ca3af'}>● </tspan>}
            <tspan fill="#f2f2f2">{ln.text}</tspan>
          </text>
        );
      })}
    </g>
  );
};

export const LapChart = ({ width, height, data, onLapClick }) => {
  const [activeRow, setActiveRow] = useState();
  const [armedRow, setArmedRow] = useState();
  const isTouch = useIsTouchDevice();

  const margin = { top: 16, right: 24, bottom: 48, left: 72 };
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);
  const innerWidth = Math.max(0, width - margin.left - margin.right);

  const allData = useMemo(() => {
    const flat = [];
    for (const stint of data) {
      for (const lap of stint.laps) {
        flat.push({
          driver: stint.driver,
          stint: stint.stint,
          compound: stint.compound,
          lapNumber: lap.lapNumber,
          lapTimeSeconds: lap.lapTimeSeconds,
        });
      }
    }
    return flat;
  }, [data]);

  const xScale = scaleLinear()
    .domain(extent(allData, xValue))
    .range([0, innerWidth])
    .nice();

  const yScale = scaleLinear()
    .domain(extent(allData, yValue))
    .range([innerHeight, 0])
    .nice();

  const colorScale = scaleOrdinal()
    .domain(['SOFT', 'MEDIUM', 'HARD', 'INTERMEDIATE', 'WET'])
    .range(['#e10600', '#f7f00c', '#f2f2f2', '#2bbf4f', '#3b82f6']);

  const lineGenerator = line()
    .x((d) => xScale(xValue(d)))
    .y((d) => yScale(yValue(d)));

  const handleVoronoiHover = useCallback((row) => setActiveRow(row), []);

  const handleTap = useCallback(
    (row) => {
      if (
        armedRow &&
        armedRow.driver === row.driver &&
        armedRow.lapNumber === row.lapNumber
      ) {
        setArmedRow(undefined);
        if (onLapClick) onLapClick(row);
      } else {
        setArmedRow(row);
        setActiveRow(row);
      }
    },
    [armedRow, onLapClick],
  );

  if (!innerWidth || !innerHeight || allData.length === 0) {
    return <svg width={width} height={height} />;
  }

  const markerX = activeRow ? lineGenerator.x()(activeRow) : 0;
  const markerY = activeRow ? lineGenerator.y()(activeRow) : 0;
  const tooltipLines = activeRow
    ? [
        { kind: 'header', text: `Lap ${activeRow.lapNumber}` },
        { kind: 'value', bullet: true, text: `${activeRow.driver} · ${activeRow.compound}` },
        { kind: 'value', text: yAxisTickFormat(activeRow.lapTimeSeconds) },
        {
          kind: 'subtext',
          text: isTouch ? 'Tap again for telemetry' : 'Click for telemetry',
        },
      ]
    : null;
  const compoundColor = activeRow ? colorScale(colorValue(activeRow)) : null;

  return (
    <svg width={width} height={height}>
      <g transform={`translate(${margin.left},${margin.top})`}>
        <AxisBottom xScale={xScale} innerHeight={innerHeight} tickOffset={5} />
        <AxisLeft
          yScale={yScale}
          innerWidth={innerWidth}
          tickFormat={yAxisTickFormat}
          tickOffset={5}
        />

        {data.map((stint, i) => (
          <g key={`stint-${stint.driver}-${stint.stint}-${i}`}>
            <Marks
              className="marker-points"
              data={stint}
              xScale={xScale}
              yScale={yScale}
              colorScale={colorScale}
              xValue={xValue}
              yValue={yValue}
              colorValue={colorValue}
              circleRadius={2}
            />
            <path className="marker-line" d={lineGenerator(stint.laps)} />
          </g>
        ))}

        {activeRow &&
          data
            .filter(
              (d) => d.driver === activeRow.driver && d.stint === activeRow.stint,
            )
            .map((stint, i) => (
              <g key={`active-${stint.driver}-${stint.stint}-${i}`}>
                <Marks
                  className="marker-points active"
                  data={stint}
                  xScale={xScale}
                  yScale={yScale}
                  colorScale={colorScale}
                  xValue={xValue}
                  yValue={yValue}
                  colorValue={colorValue}
                  circleRadius={2.5}
                />
                <path className="marker-line active" d={lineGenerator(stint.laps)} />
                <g transform={`translate(${markerX},${markerY})`}>
                  <circle r={6} fill="none" stroke="#f2f2f2" strokeWidth={1.5} />
                  {isTouch && armedRow && (
                    <circle r={10} fill="none" stroke="#e10600" strokeWidth={2} />
                  )}
                </g>
              </g>
            ))}

        {activeRow && tooltipLines && (
          <BoxedTooltip
            markerX={markerX}
            markerY={markerY}
            innerWidth={innerWidth}
            innerHeight={innerHeight}
            lines={tooltipLines}
            compoundColor={compoundColor}
          />
        )}

        <VoronoiOverlay
          onHover={handleVoronoiHover}
          onSelect={isTouch ? undefined : onLapClick}
          onTap={isTouch ? handleTap : undefined}
          innerWidth={innerWidth}
          innerHeight={innerHeight}
          allData={allData}
          lineGenerator={lineGenerator}
        />
      </g>
    </svg>
  );
};
